import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import React from 'react';
import { css } from '@emotion/react';
import { Select } from '@redux-devtools/ui';
import { StyleUtilsContext } from '../styles/themes.js';
import debounce from 'lodash.debounce';
import { sortQueryOptions } from '../utils/comparators.js';
import { filterQueryOptions } from '../utils/filters.js';
import { SortOrderButton } from './SortOrderButton.js';
import { RegexIcon } from './RegexIcon.js';
const srOnlyCss = css({
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0,0,0,0)',
    border: 0,
});
const selectId = 'rtk-query-comp-select';
const searchId = 'rtk-query-search-query';
const filterSelectId = 'rtk-query-search-query-select';
const searchPlaceholder = 'filter query by...';
const labels = {
    regexToggle: {
        info: 'Use regular expression search',
        error: 'Invalid regular expression provided',
    },
};
export class QueryForm extends React.PureComponent {
    constructor(props) {
        super(props);
        this.inputSearchRef = React.createRef();
        this.handleSubmit = (evt) => {
            evt.preventDefault();
        };
        this.handleButtonGroupClick = (isAsc) => {
            this.props.onFormValuesChange({ isAscendingQueryComparatorOrder: isAsc });
        };
        this.handleSelectComparatorChange = (option) => {
            if (typeof option?.value === 'string') {
                this.props.onFormValuesChange({ queryComparator: option.value });
            }
        };
        this.handleSelectFilterChange = (option) => {
            if (typeof option?.value === 'string') {
                this.props.onFormValuesChange({ queryFilter: option.value });
            }
        };
        this.handleRegexSearchClick = () => {
            this.props.onFormValuesChange({
                isRegexSearch: !this.props.values.isRegexSearch,
            });
        };
        this.restoreCaretPosition = (start, end) => {
            window.requestAnimationFrame(() => {
                if (this.inputSearchRef.current) {
                    this.inputSearchRef.current.selectionStart = start;
                    this.inputSearchRef.current.selectionEnd = end;
                }
            });
        };
        this.invalidateSearchValueFromProps = debounce(() => {
            this.props.onFormValuesChange({
                searchValue: this.state.searchValue,
            });
        }, 150);
        this.handleSearchChange = (evt) => {
            const searchValue = evt.target.value.trim();
            this.setState({ searchValue });
            this.invalidateSearchValueFromProps();
        };
        this.handleClearSearchClick = (evt) => {
            evt.preventDefault();
            if (this.state.searchValue) {
                this.setState({ searchValue: '' });
                this.invalidateSearchValueFromProps();
            }
        };
        this.state = {
            searchValue: props.values.searchValue,
        };
    }
    render() {
        const { searchQueryRegex, values: { isAscendingQueryComparatorOrder: isAsc, queryComparator, searchValue, queryFilter, isRegexSearch, }, } = this.props;
        const isRegexInvalid = isRegexSearch && searchValue.length > 0 && searchQueryRegex == null;
        const regexToggleType = isRegexInvalid ? 'error' : 'info';
        const regexToggleLabel = labels.regexToggle[regexToggleType];
        return (_jsx(StyleUtilsContext.Consumer, { children: ({ base16Theme }) => {
                return (_jsxs("form", { id: "rtk-query-monitor-query-selection-form", action: "#", onSubmit: this.handleSubmit, css: {
                        display: 'flex',
                        flexFlow: 'column nowrap',
                    }, children: [_jsxs("div", { css: (theme) => ({
                                display: 'flex',
                                padding: 4,
                                flex: '0 0 auto',
                                alignItems: 'center',
                                borderBottomWidth: '1px',
                                borderBottomStyle: 'solid',
                                borderColor: theme.LIST_BORDER_COLOR,
                            }), children: [_jsx("label", { htmlFor: searchId, css: srOnlyCss, children: "filter query" }), _jsxs("div", { css: (theme) => ({
                                        maxWidth: '65%',
                                        backgroundColor: theme.BACKGROUND_COLOR,
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexFlow: 'row nowrap',
                                        flex: '1 1 auto',
                                        paddingRight: 6,
                                        '& input': {
                                            outline: 'none',
                                            border: 'none',
                                            width: '100%',
                                            flex: '1 1 auto',
                                            padding: '5px 10px',
                                            fontSize: '1em',
                                            position: 'relative',
                                            fontFamily: 'monaco, Consolas, "Lucida Console", monospace',
                                            backgroundColor: theme.BACKGROUND_COLOR,
                                            color: theme.TEXT_COLOR,
                                            '&::-webkit-input-placeholder': {
                                                color: theme.TEXT_PLACEHOLDER_COLOR,
                                            },
                                            '&::-moz-placeholder': {
                                                color: theme.TEXT_PLACEHOLDER_COLOR,
                                            },
                                            '&::-webkit-search-cancel-button': {
                                                WebkitAppearance: 'none',
                                            },
                                        },
                                    }), children: [_jsx("input", { ref: this.inputSearchRef, type: "search", value: this.state.searchValue, onChange: this.handleSearchChange, placeholder: searchPlaceholder }), _jsx("button", { type: "reset", "aria-label": "clear search", "data-invisible": +(this.state.searchValue.length === 0) || undefined, onClick: this.handleClearSearchClick, css: (theme) => ({
                                                WebkitAppearance: 'none',
                                                border: 'none',
                                                outline: 'none',
                                                boxShadow: 'none',
                                                display: 'block',
                                                flex: '0 0 auto',
                                                cursor: 'pointer',
                                                background: 'transparent',
                                                position: 'relative',
                                                fontSize: 'inherit',
                                                '&[data-invisible="1"]': {
                                                    visibility: 'hidden !important',
                                                },
                                                '&::after': {
                                                    content: '"\u00d7"',
                                                    display: 'block',
                                                    padding: 4,
                                                    fontSize: '1.2em',
                                                    color: theme.TEXT_PLACEHOLDER_COLOR,
                                                    background: 'transparent',
                                                },
                                                '&:hover::after': {
                                                    color: theme.TEXT_COLOR,
                                                },
                                            }) }), _jsx("button", { type: "button", "aria-label": regexToggleLabel, title: regexToggleLabel, "data-type": regexToggleType, "aria-pressed": isRegexSearch, onClick: this.handleRegexSearchClick, css: (theme) => ({
                                                width: '24px',
                                                height: '24px',
                                                display: 'inline-block',
                                                flex: '0 0 auto',
                                                color: theme.TEXT_PLACEHOLDER_COLOR,
                                                cursor: 'pointer',
                                                padding: 0,
                                                fontSize: '0.7em',
                                                letterSpacing: '-0.7px',
                                                outline: 'none',
                                                boxShadow: 'none',
                                                fontWeight: '700',
                                                border: 'none',
                                                '&:hover': {
                                                    color: theme.TEXT_COLOR,
                                                },
                                                backgroundColor: 'transparent',
                                                '&[aria-pressed="true"]': {
                                                    color: theme.BACKGROUND_COLOR,
                                                    backgroundColor: theme.TEXT_COLOR,
                                                },
                                                '&[data-type="error"]': {
                                                    color: theme.TEXT_COLOR,
                                                    backgroundColor: theme.TOGGLE_BUTTON_ERROR,
                                                },
                                            }), children: _jsx(RegexIcon, {}) })] }), _jsx("label", { htmlFor: selectId, css: srOnlyCss, children: "filter by" }), _jsx(Select, { id: filterSelectId, isSearchable: false, options: filterQueryOptions, theme: base16Theme, value: filterQueryOptions.find((opt) => opt?.value === queryFilter), onChange: this.handleSelectFilterChange })] }), _jsxs("div", { css: {
                                display: 'flex',
                                padding: '0.4em',
                                '& label': {
                                    display: 'flex',
                                    flex: '0 0 auto',
                                    whiteSpace: 'nowrap',
                                    alignItems: 'center',
                                    paddingRight: '0.4em',
                                },
                                '& > :last-child': {
                                    flex: '0 0 auto',
                                    marginLeft: '0.4em',
                                },
                            }, children: [_jsx("label", { htmlFor: selectId, children: "Sort by" }), _jsx(Select, { id: selectId, isSearchable: false, theme: base16Theme, value: sortQueryOptions.find((opt) => opt?.value === queryComparator), options: sortQueryOptions, onChange: this.handleSelectComparatorChange }), _jsx(SortOrderButton, { id: 'rtk-query-sort-order-button', isAsc: isAsc, onChange: this.handleButtonGroupClick })] })] }));
            } }));
    }
}
